import { html, LitElement } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'

import componentStyle from '../common/styles/Component.css'
import style from './ScrollArea.css'

/** Time (ms) the `data-scrolling` state lingers after the last scroll. */
const SCROLL_TIMEOUT = 500
/** Smallest the thumb is allowed to shrink to, in pixels. */
const MIN_THUMB_SIZE = 16

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/** Computed margin/padding offset of an element along an axis, like Base UI's `getOffset`. */
function getOffset(element: Element | null, prop: 'margin' | 'padding', axis: 'x' | 'y'): number {
  if (!element) {
    return 0
  }
  const styles = getComputedStyle(element)
  const side = axis === 'x' ? 'Inline' : 'Block'
  return Number.parseFloat(styles[`${prop}${side}Start` as never]) + Number.parseFloat(styles[`${prop}${side}End` as never])
}

/**
 * Scroll Area is a container that wraps overflowing content in a custom scroll
 * viewport with consistently-styled scrollbars. Native scrollbars are hidden and
 * replaced with a thin overlay thumb that appears on hover or while scrolling.
 *
 * It also exposes the distance from each edge as CSS custom properties
 * (`--scroll-area-overflow-*`), which can drive an edge-fade mask on
 * `::part(viewport)` — see the docs example.
 *
 * The scroll machinery lives in shadow DOM; slot your content directly inside.
 *
 * @status ready
 * @category layout
 *
 * @slot - The scrollable content.
 *
 * @csspart viewport - The scrolling container that holds the slotted content.
 * @csspart scrollbar-block - The scrollbar track for the block (vertical) axis.
 * @csspart scrollbar-inline - The scrollbar track for the inline (horizontal) axis.
 * @csspart thumb-block - The draggable thumb of the block-axis scrollbar.
 * @csspart thumb-inline - The draggable thumb of the inline-axis scrollbar.
 * @csspart corner - The square where the two scrollbars meet (when `axis="both"`).
 *
 * @cssprop [--n-scroll-area-fade-size=24px] - Depth of the edge fade (when `fade` is set).
 * @cssprop [--n-scroll-area-scrollbar-size=10px] - Thickness of the scrollbar track and thumb.
 * @cssprop [--n-scroll-area-scrollbar-inset=2px] - Inset of the scrollbar from the viewport edge.
 * @cssprop [--n-scroll-area-thumb-color] - Thumb fill (defaults to the border token).
 * @cssprop [--n-scroll-area-thumb-color-hover] - Thumb fill on hover (defaults to the strong border token).
 * @cssprop [--scroll-area-overflow-y-start] - Read-only: pixels scrolled past the block start edge.
 * @cssprop [--scroll-area-overflow-y-end] - Read-only: pixels remaining to the block end edge.
 * @cssprop [--scroll-area-overflow-x-start] - Read-only: pixels scrolled past the inline start edge.
 * @cssprop [--scroll-area-overflow-x-end] - Read-only: pixels remaining to the inline end edge.
 */
@customElement('nord-scroll-area')
export default class ScrollArea extends LitElement {
  static styles = [componentStyle, style]

  /**
   * Which scrollbars to render. `block` is vertical, `inline` is horizontal.
   * @default "block"
   */
  @property({ reflect: true }) axis: 'block' | 'inline' | 'both' = 'block'

  /**
   * Distance in pixels past an edge before the matching `data-overflow-*` state
   * (and edge-fade variable) is considered active.
   * @default 0
   */
  @property({ type: Number, attribute: 'overflow-edge-threshold' }) overflowEdgeThreshold = 0

  /**
   * Fades the content in and out at the scroll edges with a mask, signalling
   * there is more content beyond the edge.
   * @default false
   */
  @property({ type: Boolean, reflect: true }) fade = false

  /** Always display the scrollbar instead of hiding it when idle. */
  @property({ type: Boolean, reflect: true, attribute: 'always-show-scrollbar' }) alwaysShowScrollbar = false

  @query('[part~="viewport"]') private viewport!: HTMLDivElement
  @query('[part~="scrollbar-block"]') private scrollbarBlock?: HTMLDivElement | null
  @query('[part~="thumb-block"]') private thumbBlock?: HTMLDivElement | null
  @query('[part~="scrollbar-inline"]') private scrollbarInline?: HTMLDivElement | null
  @query('[part~="thumb-inline"]') private thumbInline?: HTMLDivElement | null

  @state() private hasBlockOverflow = false
  @state() private hasInlineOverflow = false

  private resizeObserver?: ResizeObserver
  private coarsePointer?: MediaQueryList
  private scrollTimeout?: ReturnType<typeof setTimeout>

  // Drag state
  private dragOrientation: 'block' | 'inline' | null = null
  private dragStart = 0
  private dragStartScroll = 0

  private get hasBlock(): boolean {
    return this.axis === 'block' || this.axis === 'both'
  }

  private get hasInline(): boolean {
    return this.axis === 'inline' || this.axis === 'both'
  }

  connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('pointerenter', this.handlePointerOver)
    this.addEventListener('pointermove', this.handlePointerOver)
    this.addEventListener('pointerleave', this.handlePointerLeave)
    this.coarsePointer = window.matchMedia('(pointer: coarse)')
    this.coarsePointer.addEventListener('change', this.handleCoarseChange)
    this.toggleAttribute('data-touch', this.coarsePointer.matches)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListener('pointerenter', this.handlePointerOver)
    this.removeEventListener('pointermove', this.handlePointerOver)
    this.removeEventListener('pointerleave', this.handlePointerLeave)
    this.coarsePointer?.removeEventListener('change', this.handleCoarseChange)
    this.resizeObserver?.disconnect()
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }
  }

  firstUpdated(): void {
    this.resizeObserver = new ResizeObserver(() => this.measure())
    this.resizeObserver.observe(this.viewport)
    // Observe the slotted content too, so growth/shrink re-measures.
    const slot = this.viewport.querySelector('slot')
    slot?.assignedElements().forEach(el => this.resizeObserver?.observe(el))
    this.measure()
  }

  protected updated(): void {
    // Re-measure when `axis` changes (scrollbars added/removed).
    this.measure()
  }

  private handleSlotChange = (): void => {
    if (!this.resizeObserver) {
      return
    }
    const slot = this.viewport.querySelector('slot')
    slot?.assignedElements().forEach(el => this.resizeObserver?.observe(el))
    this.measure()
  }

  private handleCoarseChange = (event: MediaQueryListEvent): void => {
    this.toggleAttribute('data-touch', event.matches)
  }

  private handlePointerOver = (event: PointerEvent): void => {
    if (event.pointerType === 'touch') {
      this.toggleAttribute('data-touch', true)
      return
    }
    this.toggleAttribute('data-hovering', true)
  }

  private handlePointerLeave = (): void => {
    this.removeAttribute('data-hovering')
  }

  /** Marks the area as actively scrolling, clearing the flag after a quiet period. */
  private flagScrolling(): void {
    this.toggleAttribute('data-scrolling', true)
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }
    this.scrollTimeout = setTimeout(() => this.removeAttribute('data-scrolling'), SCROLL_TIMEOUT)
  }

  private handleScroll = (): void => {
    this.flagScrolling()
    this.measure()
  }

  /**
   * Measures overflow and positions/sizes the thumbs. Ported from Base UI's
   * `ScrollAreaViewport#computeThumbPosition`.
   */
  private measure(): void {
    const viewport = this.viewport
    if (!viewport) {
      return
    }

    const { scrollHeight, clientHeight, scrollTop, scrollWidth, clientWidth, scrollLeft } = viewport
    const hasBlockOverflow = scrollHeight > clientHeight
    const hasInlineOverflow = scrollWidth > clientWidth

    this.hasBlockOverflow = hasBlockOverflow
    this.hasInlineOverflow = hasInlineOverflow
    this.toggleAttribute('data-has-overflow-y', hasBlockOverflow)
    this.toggleAttribute('data-has-overflow-x', hasInlineOverflow)

    const maxScrollTop = Math.max(0, scrollHeight - clientHeight)
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    const topFromStart = hasBlockOverflow ? clamp(scrollTop, 0, maxScrollTop) : 0
    const topFromEnd = hasBlockOverflow ? maxScrollTop - topFromStart : 0
    const leftFromStart = hasInlineOverflow ? clamp(scrollLeft, 0, maxScrollLeft) : 0
    const leftFromEnd = hasInlineOverflow ? maxScrollLeft - leftFromStart : 0

    // Read-only overflow distances (drive consumer edge-fade masks on ::part(viewport)).
    viewport.style.setProperty('--scroll-area-overflow-y-start', `${topFromStart}px`)
    viewport.style.setProperty('--scroll-area-overflow-y-end', `${topFromEnd}px`)
    viewport.style.setProperty('--scroll-area-overflow-x-start', `${leftFromStart}px`)
    viewport.style.setProperty('--scroll-area-overflow-x-end', `${leftFromEnd}px`)

    const t = this.overflowEdgeThreshold
    this.toggleAttribute('data-overflow-y-start', hasBlockOverflow && topFromStart > t)
    this.toggleAttribute('data-overflow-y-end', hasBlockOverflow && topFromEnd > t)
    this.toggleAttribute('data-overflow-x-start', hasInlineOverflow && leftFromStart > t)
    this.toggleAttribute('data-overflow-x-end', hasInlineOverflow && leftFromEnd > t)

    // Block (vertical) thumb sizing + position.
    if (this.hasBlock && this.scrollbarBlock && this.thumbBlock) {
      const trackOffset = getOffset(this.scrollbarBlock, 'padding', 'y')
      const thumbOffset = getOffset(this.thumbBlock, 'margin', 'y')
      const track = this.scrollbarBlock.offsetHeight - trackOffset - thumbOffset
      const thumbHeight = Math.max(MIN_THUMB_SIZE, track * (clientHeight / scrollHeight))
      this.scrollbarBlock.style.setProperty('--scroll-area-thumb-height', `${thumbHeight}px`)
      const maxThumbOffset = track - thumbHeight
      const ratio = maxScrollTop === 0 ? 0 : scrollTop / maxScrollTop
      const offset = clamp(ratio * maxThumbOffset, 0, maxThumbOffset)
      this.thumbBlock.style.transform = `translate3d(0, ${offset}px, 0)`
    }

    // Inline (horizontal) thumb sizing + position.
    if (this.hasInline && this.scrollbarInline && this.thumbInline) {
      const trackOffset = getOffset(this.scrollbarInline, 'padding', 'x')
      const thumbOffset = getOffset(this.thumbInline, 'margin', 'x')
      const track = this.scrollbarInline.offsetWidth - trackOffset - thumbOffset
      const thumbWidth = Math.max(MIN_THUMB_SIZE, track * (clientWidth / scrollWidth))
      this.scrollbarInline.style.setProperty('--scroll-area-thumb-width', `${thumbWidth}px`)
      const maxThumbOffset = track - thumbWidth
      const ratio = maxScrollLeft === 0 ? 0 : scrollLeft / maxScrollLeft
      const offset = clamp(ratio * maxThumbOffset, 0, maxThumbOffset)
      this.thumbInline.style.transform = `translate3d(${offset}px, 0, 0)`
    }
  }

  // --- Thumb dragging -------------------------------------------------------

  private handleThumbPointerDown(event: PointerEvent, orientation: 'block' | 'inline'): void {
    if (event.button !== 0) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    this.dragOrientation = orientation
    this.dragStart = orientation === 'block' ? event.clientY : event.clientX
    this.dragStartScroll = orientation === 'block' ? this.viewport.scrollTop : this.viewport.scrollLeft
    ;(event.target as HTMLElement).setPointerCapture(event.pointerId)
    window.addEventListener('pointermove', this.handleThumbPointerMove)
    window.addEventListener('pointerup', this.handleThumbPointerUp)
  }

  private handleThumbPointerMove = (event: PointerEvent): void => {
    if (!this.dragOrientation) {
      return
    }
    event.preventDefault()
    const viewport = this.viewport
    if (this.dragOrientation === 'block' && this.scrollbarBlock && this.thumbBlock) {
      const delta = event.clientY - this.dragStart
      const trackOffset = getOffset(this.scrollbarBlock, 'padding', 'y')
      const thumbOffset = getOffset(this.thumbBlock, 'margin', 'y')
      const maxThumbOffset = this.scrollbarBlock.offsetHeight - this.thumbBlock.offsetHeight - trackOffset - thumbOffset
      const ratio = maxThumbOffset === 0 ? 0 : delta / maxThumbOffset
      viewport.scrollTop = this.dragStartScroll + ratio * (viewport.scrollHeight - viewport.clientHeight)
    }
    else if (this.dragOrientation === 'inline' && this.scrollbarInline && this.thumbInline) {
      const delta = event.clientX - this.dragStart
      const trackOffset = getOffset(this.scrollbarInline, 'padding', 'x')
      const thumbOffset = getOffset(this.thumbInline, 'margin', 'x')
      const maxThumbOffset = this.scrollbarInline.offsetWidth - this.thumbInline.offsetWidth - trackOffset - thumbOffset
      const ratio = maxThumbOffset === 0 ? 0 : delta / maxThumbOffset
      viewport.scrollLeft = this.dragStartScroll + ratio * (viewport.scrollWidth - viewport.clientWidth)
    }
    this.flagScrolling()
  }

  private handleThumbPointerUp = (): void => {
    this.dragOrientation = null
    window.removeEventListener('pointermove', this.handleThumbPointerMove)
    window.removeEventListener('pointerup', this.handleThumbPointerUp)
  }

  // --- Track click (page jump) ---------------------------------------------

  private handleTrackPointerDown(event: PointerEvent, orientation: 'block' | 'inline'): void {
    if (event.button !== 0) {
      return
    }
    const viewport = this.viewport
    if (orientation === 'block' && this.scrollbarBlock && this.thumbBlock) {
      const trackOffset = getOffset(this.scrollbarBlock, 'padding', 'y')
      const thumbOffset = getOffset(this.thumbBlock, 'margin', 'y')
      const thumbHeight = this.thumbBlock.offsetHeight
      const rect = this.scrollbarBlock.getBoundingClientRect()
      const clickY = event.clientY - rect.top - thumbHeight / 2 - trackOffset + thumbOffset / 2
      const maxThumbOffset = this.scrollbarBlock.offsetHeight - thumbHeight - trackOffset - thumbOffset
      const ratio = maxThumbOffset === 0 ? 0 : clickY / maxThumbOffset
      viewport.scrollTop = clamp(ratio, 0, 1) * (viewport.scrollHeight - viewport.clientHeight)
    }
    else if (orientation === 'inline' && this.scrollbarInline && this.thumbInline) {
      const trackOffset = getOffset(this.scrollbarInline, 'padding', 'x')
      const thumbOffset = getOffset(this.thumbInline, 'margin', 'x')
      const thumbWidth = this.thumbInline.offsetWidth
      const rect = this.scrollbarInline.getBoundingClientRect()
      const clickX = event.clientX - rect.left - thumbWidth / 2 - trackOffset + thumbOffset / 2
      const maxThumbOffset = this.scrollbarInline.offsetWidth - thumbWidth - trackOffset - thumbOffset
      const ratio = maxThumbOffset === 0 ? 0 : clickX / maxThumbOffset
      viewport.scrollLeft = clamp(ratio, 0, 1) * (viewport.scrollWidth - viewport.clientWidth)
    }
  }

  /** Scrolling over a scrollbar track scrolls the viewport. */
  private handleScrollbarWheel(event: WheelEvent, orientation: 'block' | 'inline'): void {
    if (event.ctrlKey) {
      return
    }
    event.preventDefault()
    if (orientation === 'block') {
      this.viewport.scrollTop += event.deltaY
    }
    else {
      this.viewport.scrollLeft += event.deltaY || event.deltaX
    }
  }

  render() {
    return html`
      <div
        part="viewport"
        tabindex=${this.hasBlockOverflow || this.hasInlineOverflow ? '0' : '-1'}
        @scroll=${this.handleScroll}
      >
        <slot @slotchange=${this.handleSlotChange}></slot>
      </div>
      ${this.hasBlock
        ? html`
            <div
              part="scrollbar-block"
              data-orientation="vertical"
              ?hidden=${!this.hasBlockOverflow}
              @pointerdown=${(e: PointerEvent) => this.handleTrackPointerDown(e, 'block')}
              @wheel=${(e: WheelEvent) => this.handleScrollbarWheel(e, 'block')}
            >
              <div
                part="thumb-block"
                @pointerdown=${(e: PointerEvent) => this.handleThumbPointerDown(e, 'block')}
              ></div>
            </div>
          `
        : ''}
      ${this.hasInline
        ? html`
            <div
              part="scrollbar-inline"
              data-orientation="horizontal"
              ?hidden=${!this.hasInlineOverflow}
              @pointerdown=${(e: PointerEvent) => this.handleTrackPointerDown(e, 'inline')}
              @wheel=${(e: WheelEvent) => this.handleScrollbarWheel(e, 'inline')}
            >
              <div
                part="thumb-inline"
                @pointerdown=${(e: PointerEvent) => this.handleThumbPointerDown(e, 'inline')}
              ></div>
            </div>
          `
        : ''}
      ${this.axis === 'both' ? html`<div part="corner" ?hidden=${!(this.hasBlockOverflow && this.hasInlineOverflow)}></div>` : ''}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-scroll-area': ScrollArea
  }
}
